'use client';

import { useCallback } from 'react';
import type { Room } from '@/types/travelers';
import { validateRoomCapacity } from '@/types/travelers';

export interface ReservationModalGuestsProps {
    rooms: Room[];
    onRoomsChange: (rooms: Room[]) => void;
    t: (key: string, fallback?: string) => string;
    getVariable: (key: string, fallback: string) => string; 
    departureDate?: Date | null;
    minChildAge?: number;
    maxRooms?: number;
}

/**
 * Book now modalına özel oda/gezgin seçici.
 * Dış kapsayıcı stilleri parent bileşende yönetilir; burada sadece içerik render edilir.
 */
export default function ReservationModalGuests({
    rooms,
    onRoomsChange,
    t,
    getVariable,
    departureDate = null,
    minChildAge = 0,
    maxRooms = 10,
}: ReservationModalGuestsProps) {
    const effectiveMinChildAge = Math.max(0, Math.min(11, Number.isFinite(minChildAge) ? Math.floor(minChildAge) : 0));

    const handleRoomUpdate = useCallback(
        (index: number, updates: Partial<Room>) => {
            if (!rooms.length || index < 0 || index >= rooms.length) return;
            const room = rooms[index];
            const updatedRoom: Room = { ...room, ...updates };
            if (updates.children !== undefined) {
                const newChildAges = [...room.childAges];
                if (updates.children > room.children) {
                    while (newChildAges.length < updates.children) newChildAges.push(effectiveMinChildAge);
                } else if (updates.children < room.children) {
                    newChildAges.splice(updates.children);
                }
                updatedRoom.childAges = newChildAges;
            }
            const next = [...rooms];
            next[index] = updatedRoom;
            onRoomsChange(next);
        },
        [rooms, onRoomsChange, effectiveMinChildAge]
    );

    const handleAdultsChange = useCallback(
        (index: number, delta: number) => {
            if (!rooms.length || index < 0 || index >= rooms.length) return;
            const room = rooms[index];
            const newAdults = Math.max(1, Math.min(3, room.adults + delta));
            const updates: Partial<Room> = { adults: newAdults };
            if (newAdults === 3) {
                updates.children = 0;
                updates.childAges = [];
            }
            handleRoomUpdate(index, updates);
        },
        [rooms, handleRoomUpdate]
    );

    const handleChildrenChange = useCallback(
        (index: number, delta: number) => {
            if (!rooms.length || index < 0 || index >= rooms.length) return;
            const room = rooms[index];
            if (room.adults >= 3) return;
            const currentTotal = room.adults + room.children;
            if (delta > 0 && currentTotal >= 3) return;
            const hasInfantOrToddler = room.childAges.some((a) => a >= 0 && a <= 2);
            const maxChildren = hasInfantOrToddler ? 2 : 1;
            const newChildren = Math.max(0, Math.min(maxChildren, room.children + delta));
            handleRoomUpdate(index, { children: newChildren });
        },
        [rooms, handleRoomUpdate]
    );

    const handleChildAgeChange = useCallback(
        (index: number, childIndex: number, age: number) => {
            if (!rooms.length || index < 0 || index >= rooms.length) return;
            const room = rooms[index];
            if (childIndex < 0 || childIndex >= room.childAges.length) return;
            const newChildAges = [...room.childAges];
            newChildAges[childIndex] = age;
            if (room.children === 2) {
                const otherAge = newChildAges[childIndex === 0 ? 1 : 0];
                const thisIs02 = age >= 0 && age <= 2;
                const otherIs02 = otherAge >= 0 && otherAge <= 2;
                if (!thisIs02 && !otherIs02) {
                    handleRoomUpdate(index, { children: 1, childAges: [newChildAges[childIndex]] });
                    return;
                }
            }
            handleRoomUpdate(index, { childAges: newChildAges });
        },
        [rooms, handleRoomUpdate]
    );

    const addRoom = useCallback(() => {
        if (rooms.length >= maxRooms) return;
        onRoomsChange([...rooms, { adults: 2, children: 0, childAges: [] }]);
    }, [rooms, maxRooms, onRoomsChange]);

    const setAdultsCount = useCallback((index: number, value: number) => {
        if (!rooms.length || index < 0 || index >= rooms.length) return;
        const nextAdults = Math.max(1, Math.min(3, value));
        const updates: Partial<Room> = { adults: nextAdults };
        if (nextAdults === 3) {
            updates.children = 0;
            updates.childAges = [];
        }
        handleRoomUpdate(index, updates);
    }, [rooms, handleRoomUpdate]);

    const setChildrenCount = useCallback((index: number, value: number) => {
        if (!rooms.length || index < 0 || index >= rooms.length) return;
        const room = rooms[index];
        if (room.adults >= 3) {
            handleRoomUpdate(index, { children: 0, childAges: [] });
            return;
        }
        const totalLimit = Math.max(0, 3 - room.adults);
        const hasInfantOrToddler = room.childAges.some((a) => a >= 0 && a <= 2);
        const toddlerRuleLimit = hasInfantOrToddler ? 2 : 1;
        const maxAllowed = Math.min(2, totalLimit, toddlerRuleLimit);
        const nextChildren = Math.max(0, Math.min(maxAllowed, value));
        handleRoomUpdate(index, { children: nextChildren });
    }, [rooms, handleRoomUpdate]);

    const removeRoom = useCallback(
        (index: number) => {
            if (rooms.length <= 1) return;
            onRoomsChange(rooms.filter((_, i) => i !== index));
        },
        [rooms, onRoomsChange]
    );

    const getChildBirthDateRange = useCallback(
        (age: number): { start: Date; end: Date } | null => {
            if (!departureDate) return null;
            const departure = new Date(departureDate);
            const endDate = new Date(departure);
            endDate.setFullYear(departure.getFullYear() - age);
            const startDate = new Date(endDate);
            startDate.setFullYear(startDate.getFullYear() - 1);
            startDate.setDate(startDate.getDate() + 1);
            return { start: startDate, end: endDate };
        },
        [departureDate]
    );

    if (!rooms.length) return null;

    return (
        <div className="w-full space-y-4" data-modal-guests-box>
                {rooms.map((room, roomIndex) => {
                    const totalPeople = room.adults + room.children;
                    const validation = validateRoomCapacity(room);
                    const isAdultsMax = room.adults === 3;

                    return (
                        <div
                            key={`modal-room-${roomIndex}`}
                            className="pb-4 last:pb-0 border-b border-[#e5e7eb] last:border-b-0"
                        >
                            <div className="flex flex-row justify-between items-center mb-3">
                                <span className="text-sm font-medium text-gray-900">
                                    {getVariable('room_number', 'Room {number}')
                                        .replace(/\{number\}/g, String(roomIndex + 1))}
                                </span>
                                {rooms.length > 1 && (
                                    <button
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            removeRoom(roomIndex);
                                        }}
                                        className="text-xs text-alsero-first hover:text-alsero-first/80 font-medium"
                                    >
                                        {getVariable('room_remove', 'Remove Room')}
                                    </button>
                                )}
                            </div>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                                        {getVariable('room_travelers_adults', 'Adults')}
                                    </label>
                                    <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden bg-white">
                                        <button
                                            type="button"
                                            onClick={() => handleAdultsChange(roomIndex, -1)}
                                            disabled={room.adults === 1}
                                            aria-label="Decrease adults"
                                            className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900 active:bg-black/[0.03]"
                                        >
                                            −
                                        </button>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={String(room.adults)}
                                            onChange={(e) => {
                                                const digits = e.target.value.replace(/\D/g, '');
                                                setAdultsCount(roomIndex, digits ? parseInt(digits, 10) : 1);
                                            }}
                                            className="flex-1 min-w-[2.5rem] py-2 text-center text-sm font-semibold text-gray-900 bg-transparent outline-none"
                                            aria-label="Adults count"
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleAdultsChange(roomIndex, 1)}
                                            disabled={room.adults === 3}
                                            aria-label="Increase adults"
                                            className="flex items-center justify-center w-10 h-10 text-gray-600 border-l border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900 active:bg-black/[0.03]"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                                <div>
                                    <label className="text-xs font-medium text-gray-600 mb-1.5 block">
                                        {getVariable('room_travelers_children', 'Children')}
                                    </label>
                                    <div className="flex items-center border border-[#e5e7eb] rounded-lg overflow-hidden bg-white">
                                        <button
                                            type="button"
                                            onClick={() => handleChildrenChange(roomIndex, -1)}
                                            disabled={room.children === 0 || isAdultsMax}
                                            aria-label="Decrease children"
                                            className="flex items-center justify-center w-10 h-10 text-gray-600 border-r border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:text-gray-900 active:bg-black/[0.03]"
                                        >
                                            −
                                        </button>
                                        <input
                                            type="text"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            value={String(room.children)}
                                            onChange={(e) => {
                                                const digits = e.target.value.replace(/\D/g, '');
                                                setChildrenCount(roomIndex, digits ? parseInt(digits, 10) : 0);
                                            }}
                                            className={`flex-1 min-w-[2.5rem] py-2 text-center text-sm font-semibold bg-transparent outline-none ${
                                                isAdultsMax ? 'text-gray-400' : 'text-gray-900'
                                            }`}
                                            aria-label="Children count"
                                            disabled={isAdultsMax}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => handleChildrenChange(roomIndex, 1)}
                                            disabled={
                                                isAdultsMax ||
                                                totalPeople >= 3 ||
                                                room.children >= 2 ||
                                                (room.children === 1 &&
                                                    !room.childAges.some((a) => a >= 0 && a <= 2))
                                            }
                                            aria-label="Increase children"
                                            className="flex items-center justify-center w-10 h-10 text-alsero-first border-l border-[#e5e7eb] disabled:opacity-40 disabled:cursor-not-allowed hover:opacity-80 active:bg-alsero-first/5"
                                        >
                                            +
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {room.children > 0 && (
                                <div className="mt-3 pt-3 border-t border-[#e5e7eb]">
                                    <label className="text-xs font-medium text-gray-600 mb-2 block">
                                        {getVariable(
                                            'room_travelers_child_age',
                                            getVariable('reservation.child_age_label', 'Child age')
                                        )}
                                    </label>
                                    <div
                                        className={
                                            room.childAges.length === 1
                                                ? 'grid grid-cols-1 gap-2'
                                                : 'grid grid-cols-2 gap-2'
                                        }
                                    >
                                        {room.childAges.map((age, childIndex) => {
                                            const birthDateRange = getChildBirthDateRange(age);
                                            return (
                                                <div key={`child-${roomIndex}-${childIndex}`}>
                                                    <select
                                                        value={age}
                                                        onChange={(e) =>
                                                            handleChildAgeChange(
                                                                roomIndex,
                                                                childIndex,
                                                                parseInt(e.target.value)
                                                            )
                                                        }
                                                        disabled={isAdultsMax}
                                                        className="w-full border border-[#e5e7eb] rounded-lg bg-white px-2 py-1.5 text-sm text-gray-900 focus:border-alsero-first focus:ring-1 focus:ring-alsero-first disabled:opacity-50"
                                                    >
                                                        {Array.from({ length: Math.max(1, 12 - effectiveMinChildAge) }, (_, i) => {
                                                            const ageValue = i + effectiveMinChildAge;
                                                            return (
                                                            <option key={ageValue} value={ageValue}>
                                                                {ageValue}{' '}
                                                                {getVariable(
                                                                    'years_old',
                                                                    getVariable(
                                                                        'reservation.years_old',
                                                                        'years'
                                                                    )
                                                                )}
                                                            </option>
                                                        )})}
                                                    </select>
                                                    {birthDateRange && departureDate && (
                                                        <span className="text-xs text-gray-500 mt-0.5 block">
                                                            {getVariable(
                                                                'reservation.child_age_birthdate_range_hint',
                                                                'Birth date between {start} - {end}'
                                                            )
                                                                .replace(
                                                                    '{start}',
                                                                    birthDateRange.start.toLocaleDateString()
                                                                )
                                                                .replace(
                                                                    '{end}',
                                                                    birthDateRange.end.toLocaleDateString()
                                                                )}
                                                        </span>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            )}

                            {validation.warnings.length > 0 && (
                                <div className="mt-2 p-2 border border-amber-200 rounded-lg bg-white">
                                    {validation.warnings.map((w, i) => (
                                        <p key={i} className="text-amber-800 text-xs">
                                            {getVariable(w, w)}
                                        </p>
                                    ))}
                                </div>
                            )}
                        </div>
                    );
                })}

                {rooms.length < maxRooms && (
                    <button
                        type="button"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            addRoom();
                        }}
                        className="w-full text-sm font-medium text-alsero-first hover:text-alsero-first/80 py-2.5 border border-dashed border-[#e5e7eb] rounded-lg hover:border-alsero-first/50 transition-colors bg-white"
                    >
                        {getVariable('room_add', '+ Add Room')}
                    </button>
                )}

                {maxRooms > 1 && rooms.length >= maxRooms && (
                    <p className="text-xs text-gray-500">
                        {getVariable(
                            'room_max_limit_warning',
                            `Maximum ${maxRooms} rooms`
                        ).replace(/\{max\}/g, String(maxRooms))}
                    </p>
                )}
        </div>
    );
}
